import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Key, LayoutDashboard, LogOut, Loader2, ArrowRight, Clock, MapPin } from 'lucide-react';
import axios from 'axios';

import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { fetchMyBookings } from '../lib/bookingsStore';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function nowBrussels() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Brussels' }));
}

function parseLocal(dateStr, timeStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
}

/**
 * Minimal mobile PWA hub. This is the `start_url` of the installed app:
 * users who tap the home-screen icon land here, NOT on the marketing site.
 * No navbar, no footer — just the essentials.
 */
export default function MobileHubPage() {
  const { t, i18n } = useTranslation();
  const { user, token, loading: authLoading, logout } = useAuth();
  const { logo } = useSiteSettings();
  const navigate = useNavigate();
  const [nextBooking, setNextBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetchMyBookings(token);
      const items = (res.bookings || []).filter((b) => b.status === 'confirmed' && b.access_code);
      const now = nowBrussels();
      const grace = 15 * 60 * 1000;
      // First booking that hasn't ended yet (with grace)
      const next = items
        .map((b) => ({ ...b, _start: parseLocal(b.date, b.start_time || '00:00'), _end: parseLocal(b.date, b.end_time || '23:59') }))
        .filter((b) => b._end && (b._end.getTime() + grace) > now.getTime())
        .sort((a, b) => (a._start?.getTime() || 0) - (b._start?.getTime() || 0))[0];
      setNextBooking(next || null);
    } catch {
      // Ignore — landing page works without bookings
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const isActive = (b) => {
    if (!b?._start || !b?._end) return false;
    const now = nowBrussels();
    const grace = 15 * 60 * 1000;
    return now.getTime() >= b._start.getTime() - grace && now.getTime() <= b._end.getTime() + grace;
  };

  return (
    <div
      className="min-h-screen bg-[#1A3C34] text-white flex flex-col"
      data-testid="mobile-hub-page"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Top bar */}
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <img src={logo} alt="Flexi" className="h-7 w-auto rounded bg-white/5 p-1" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user && (
            <button
              type="button"
              onClick={async () => { await logout(); navigate('/'); }}
              data-testid="hub-logout-btn"
              aria-label="Logout"
              className="text-white/50 hover:text-white p-2"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 px-5 pb-8 max-w-md mx-auto w-full">
        {/* Greeting */}
        <div className="mb-6 mt-2">
          <h1 className="font-heading text-2xl font-bold leading-tight">
            {user ? t('hub.helloUser', { name: user.name?.split(' ')[0] || '' }) : t('hub.helloGuest', 'Bienvenue')}
          </h1>
          <p className="text-sm text-white/50 mt-1">
            {t('hub.tagline', 'Que souhaitez-vous faire ?')}
          </p>
        </div>

        {/* Active booking spotlight */}
        {!authLoading && !loading && nextBooking && isActive(nextBooking) && (
          <button
            type="button"
            onClick={() => navigate('/mes-acces')}
            data-testid="hub-active-card"
            className="w-full bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-5 mb-4 text-left shadow-xl shadow-green-900/30 active:scale-[0.98] transition-transform"
          >
            <p className="text-[10px] uppercase tracking-widest text-white/80 font-semibold mb-1">
              {t('hub.activeNow', 'Réservation en cours')}
            </p>
            <p className="font-heading text-xl font-bold mb-1 truncate">{nextBooking.space_name}</p>
            <div className="flex items-center gap-3 text-xs text-white/80">
              <span className="inline-flex items-center gap-1"><Clock size={11} />{nextBooking.start_time}–{nextBooking.end_time}</span>
              <span className="font-mono font-bold tracking-wider">{nextBooking.access_code}</span>
            </div>
            <p className="text-xs text-white/70 mt-2 inline-flex items-center gap-1">
              {t('hub.openDoorCta', 'Ouvrir la porte')} <ArrowRight size={12} />
            </p>
          </button>
        )}

        {/* Next upcoming booking (not active yet) */}
        {!authLoading && !loading && nextBooking && !isActive(nextBooking) && (
          <button
            type="button"
            onClick={() => navigate('/mes-acces')}
            data-testid="hub-upcoming-card"
            className="w-full bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4 mb-4 text-left active:bg-white/10 transition-colors"
          >
            <p className="text-[10px] uppercase tracking-widest text-[#C77D63] font-semibold mb-1">
              {t('hub.nextBooking', 'Prochaine réservation')}
            </p>
            <p className="font-heading text-base font-bold mb-1 truncate">{nextBooking.space_name}</p>
            <div className="flex items-center gap-3 text-xs text-white/60">
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} />
                {nextBooking._start?.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' })}
              </span>
              <span className="inline-flex items-center gap-1"><Clock size={11} />{nextBooking.start_time}</span>
            </div>
          </button>
        )}

        {/* Primary actions */}
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={() => navigate(user ? '/quick-book' : '/auth?redirect=/quick-book')}
            data-testid="hub-quick-book-btn"
            className="w-full bg-[#C77D63] hover:bg-[#B86F58] active:scale-[0.98] transition-all rounded-2xl p-5 text-left flex items-center gap-4 shadow-lg shadow-black/20"
          >
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <CalendarPlus size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg font-bold leading-tight">{t('hub.quickBook', 'Réservation rapide')}</p>
              <p className="text-xs text-white/80 mt-0.5">{t('hub.quickBookSub', 'En 4 étapes')}</p>
            </div>
            <ArrowRight size={18} className="text-white/70 flex-shrink-0" />
          </button>

          <button
            type="button"
            onClick={() => navigate(user ? '/mes-acces' : '/auth?redirect=/mes-acces')}
            data-testid="hub-my-access-btn"
            className="w-full bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all border border-white/10 rounded-2xl p-5 text-left flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Key size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg font-bold leading-tight">{t('hub.myAccess', 'Mes accès')}</p>
              <p className="text-xs text-white/60 mt-0.5">{t('hub.myAccessSub', 'Codes & ouverture porte')}</p>
            </div>
            <ArrowRight size={18} className="text-white/40 flex-shrink-0" />
          </button>

          {user && (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              data-testid="hub-dashboard-btn"
              className="w-full bg-white/5 hover:bg-white/10 active:scale-[0.98] transition-all border border-white/10 rounded-2xl p-5 text-left flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <LayoutDashboard size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-lg font-bold leading-tight">{t('hub.dashboard', 'Tableau de bord')}</p>
                <p className="text-xs text-white/60 mt-0.5">{t('hub.dashboardSub', 'Historique & abonnement')}</p>
              </div>
              <ArrowRight size={18} className="text-white/40 flex-shrink-0" />
            </button>
          )}

          {!user && !authLoading && (
            <button
              type="button"
              onClick={() => navigate('/auth')}
              data-testid="hub-login-btn"
              className="w-full bg-white text-[#1A3C34] hover:bg-white/90 active:scale-[0.98] transition-all rounded-2xl p-4 text-center font-bold mt-2"
            >
              {t('hub.login', 'Connexion / Inscription')}
            </button>
          )}
        </div>

        {/* Loading shimmer */}
        {(authLoading || loading) && (
          <div className="flex items-center justify-center py-6 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {/* Footer mini */}
        <div className="mt-auto pt-10 text-center">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="hub-website-link"
            className="text-xs text-white/40 hover:text-white/60 underline-offset-4 hover:underline"
          >
            {t('hub.fullSite', 'Voir le site complet')}
          </button>
        </div>
      </main>
    </div>
  );
}
